/**
 * CiaoConnecting — Beautiful loading animation with rotating status phrases
 * Shown while the chat session is being established
 */

import React, { useState, useEffect } from 'react';

const PHRASES = [
  'Conectando con el servidor…',
  'Recopilando datos del local…',
  'Cargando agentes de inteligencia artificial…',
  'Preparando asistentes especializados…',
  'Casi listo…',
];

const PHRASE_INTERVAL = 2800;

const CiaoConnecting: React.FC = () => {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setPhraseIdx((prev) => (prev + 1) % PHRASES.length);
        setFade(true);
      }, 350);
    }, PHRASE_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      className="ciao-animate-fade-in"
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 32,
        padding: '40px 20px',
      }}
    >
      {/* Orbital animation */}
      <div className="ciao-connecting-orbit" style={{ position: 'relative', width: 80, height: 80 }}>
        {/* Center glow */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div className="ciao-connecting-core" style={{
            width: 36,
            height: 36,
            borderRadius: 12,
            background: 'linear-gradient(135deg, #FC7556, #ff9a76)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            color: '#fff',
            boxShadow: '0 4px 24px rgba(252, 117, 86, 0.35)',
          }}>
            ✦
          </div>
        </div>

        {/* Orbiting ring */}
        <div className="ciao-connecting-ring" style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: '2px solid transparent',
          borderTopColor: '#FC7556',
          borderRightColor: 'rgba(252, 117, 86, 0.3)',
        }} />

        {/* Secondary ring (counter-rotation) */}
        <div className="ciao-connecting-ring-reverse" style={{
          position: 'absolute',
          inset: 6,
          borderRadius: '50%',
          border: '1.5px solid transparent',
          borderBottomColor: 'rgba(252, 117, 86, 0.5)',
          borderLeftColor: 'rgba(252, 117, 86, 0.15)',
        }} />

        {/* Orbiting dots */}
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="ciao-connecting-dot"
            style={{
              position: 'absolute',
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#FC7556',
              top: '50%',
              left: '50%',
              marginTop: -3,
              marginLeft: -3,
              animationDelay: `${i * -0.8}s`,
              opacity: 0.8 - i * 0.2,
            }}
          />
        ))}
      </div>

      {/* Rotating phrases */}
      <div style={{
        textAlign: 'center',
        minHeight: 40,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
      }}>
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--text-color, #555)',
            opacity: fade ? 0.8 : 0,
            transition: 'opacity 0.35s ease',
            letterSpacing: '0.01em',
          }}
        >
          {PHRASES[phraseIdx]}
        </span>

        {/* Subtle progress dots */}
        <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
          {PHRASES.map((_, i) => (
            <div
              key={i}
              style={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                background: i === phraseIdx ? '#FC7556' : 'var(--text-color, #999)',
                opacity: i === phraseIdx ? 0.8 : 0.15,
                transition: 'all 0.35s ease',
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes ciao-orbit-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes ciao-orbit-spin-reverse {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }
        @keyframes ciao-orbit-dot {
          0%   { transform: rotate(0deg)   translateX(38px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(38px) rotate(-360deg); }
        }
        @keyframes ciao-core-pulse {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 24px rgba(252, 117, 86, 0.35); }
          50%      { transform: scale(1.08); box-shadow: 0 6px 32px rgba(252, 117, 86, 0.5); }
        }

        .ciao-connecting-ring {
          animation: ciao-orbit-spin 2.4s linear infinite;
        }
        .ciao-connecting-ring-reverse {
          animation: ciao-orbit-spin-reverse 3.6s linear infinite;
        }
        .ciao-connecting-dot {
          animation: ciao-orbit-dot 2.4s linear infinite;
        }
        .ciao-connecting-core {
          animation: ciao-core-pulse 2.4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default CiaoConnecting;
